// src/components/common/aia/AIAvatar.js
import React, { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { motion, useMotionValue, useSpring, useTransform, AnimatePresence } from 'framer-motion';
import { useAIAvatar } from './AIAvatarContext';
import { getTourById } from './tours';
import { useNavigate } from 'react-router-dom';
import { useChat } from './context/ChatContext';
import { X, Minimize2, CheckCircle2, Circle, ChevronRight, ChevronDown, MessageSquare, Route, Send, Sparkles, Plus } from 'lucide-react';

const HELP_TOOLTIP_VIEWS_KEY = 'oakhr_help_tooltip_views';
const MAX_TOOLTIP_VIEWS = 3;

const getSuggestedPrompts = (role) => {
    if (role === 'admin') return [
        { label: 'Pending approvals', prompt: 'Show me all pending approvals with details' },
        { label: 'Who\'s on leave today?', prompt: 'Who is on leave today?' },
        { label: 'Company headcount', prompt: 'Show department-wise headcount analytics' },
        { label: 'Upcoming holidays', prompt: 'What are the upcoming holidays?' },
        { label: 'Payroll status', prompt: 'What is the payroll status for this month?' },
        { label: 'Onboarding status', prompt: 'Show employees currently in onboarding' },
    ];
    if (role === 'manager') return [
        { label: 'Pending approvals', prompt: 'Show me all pending approvals with details' },
        { label: 'Who\'s on leave?', prompt: 'Who is on leave today from my team?' },
        { label: 'Team attendance', prompt: 'Show my team attendance for today' },
        { label: 'Upcoming holidays', prompt: 'What are the upcoming holidays?' },
        { label: 'My leaves', prompt: 'What is my leave balance?' },
        { label: 'My payslips', prompt: 'Show my recent payslips' },
    ];
    // Employee
    return [
        { label: 'My leave balance', prompt: 'What is my leave balance?' },
        { label: 'My attendance', prompt: 'Show my attendance for this month' },
        { label: 'Upcoming holidays', prompt: 'What are the upcoming holidays?' },
        { label: 'My payslips', prompt: 'Show my recent payslips' },
    ];
};

// Simple markdown-like renderer for AI responses
const renderMarkdown = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    const elements = [];
    let inList = false;
    let listItems = [];

    const flushList = () => {
        if (listItems.length > 0) {
            elements.push(
                <ul key={`list-${elements.length}`} className="list-disc list-inside space-y-0.5 my-1">
                    {listItems.map((item, i) => <li key={i} className="text-xs leading-relaxed">{formatInline(item)}</li>)}
                </ul>
            );
            listItems = [];
            inList = false;
        }
    };

    const formatInline = (str) => {
        // Bold: **text**
        const parts = str.split(/(\*\*[^*]+\*\*)/g);
        return parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
            }
            return part;
        });
    };

    lines.forEach((line, idx) => {
        const trimmed = line.trim();
        if (!trimmed) {
            flushList();
            return;
        }

        // Bullet list items
        if (/^[-*•]\s/.test(trimmed)) {
            inList = true;
            listItems.push(trimmed.replace(/^[-*•]\s/, ''));
            return;
        }

        // Numbered list items
        if (/^\d+[.)]\s/.test(trimmed)) {
            inList = true;
            listItems.push(trimmed.replace(/^\d+[.)]\s/, ''));
            return;
        }

        flushList();

        // Heading-like lines (### or ## or #)
        if (/^#{1,3}\s/.test(trimmed)) {
            elements.push(
                <p key={idx} className="font-semibold text-xs mt-1.5 mb-0.5">{formatInline(trimmed.replace(/^#{1,3}\s/, ''))}</p>
            );
            return;
        }

        elements.push(
            <p key={idx} className="text-xs leading-relaxed">{formatInline(trimmed)}</p>
        );
    });

    flushList();
    return elements;
};

// ─── Tool Result Card Components ──────────────────────────────────────────
const ToolResultCard = ({ toolCall, onConfirm, onCancel }) => {
    if (toolCall.status === 'error') return null;

    const { name, result } = toolCall;

    if (name === 'get_leave_balance') {
        if (result.type === 'company_summary') {
            return (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 mt-2 space-y-2">
                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">Company Leave Summary ({result.year})</p>
                    {result.leave_types?.map((lt, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                            <span className="text-gray-600 dark:text-gray-400">{lt.type}</span>
                            <div className="flex gap-3 text-gray-500 dark:text-gray-400">
                                <span>Ent: {lt.totalEntitled}</span>
                                <span className="text-orange-500">Used: {lt.totalUsed}</span>
                                <span className="text-blue-500">Rem: {lt.totalRemaining}</span>
                            </div>
                        </div>
                    ))}
                </div>
            );
        }
        if (result.found === false) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3 mt-2">
                    <p className="text-xs text-red-600 dark:text-red-400">{result.message}</p>
                </div>
            );
        }
        return (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 mt-2">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    {result.employee} — Leave Balance ({result.year})
                </p>
                {result.balances?.map((b, i) => (
                    <div key={i} className="flex items-center gap-2 mb-1.5 last:mb-0">
                        <div className="flex-1">
                            <div className="flex justify-between text-xs mb-1">
                                <span className="text-gray-600 dark:text-gray-400">{b.type}</span>
                                <span className="text-gray-800 dark:text-gray-200 font-medium">{b.remaining} / {b.entitled}</span>
                            </div>
                            <div className="h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                                <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-400" style={{ width: `${Math.min((b.used / b.entitled) * 100, 100)}%` }} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (name === 'get_attendance_summary') {
        if (result.message) {
            return (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 mt-2">
                    <p className="text-xs text-blue-600 dark:text-blue-400">{result.message}</p>
                </div>
            );
        }
        return (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 mt-2 space-y-2">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Attendance — {result.date} ({result.department})
                </p>
                <div className="grid grid-cols-2 gap-2">
                    <div className="bg-green-100 dark:bg-green-900/30 rounded-lg p-2 text-center">
                        <div className="text-sm font-bold text-green-600 dark:text-green-400">{result.present}</div>
                        <div className="text-[10px] text-green-600 dark:text-green-400">Present</div>
                    </div>
                    <div className="bg-red-100 dark:bg-red-900/30 rounded-lg p-2 text-center">
                        <div className="text-sm font-bold text-red-600 dark:text-red-400">{result.absent}</div>
                        <div className="text-[10px] text-red-600 dark:text-red-400">Absent</div>
                    </div>
                    <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg p-2 text-center">
                        <div className="text-sm font-bold text-blue-600 dark:text-blue-400">{result.onLeave}</div>
                        <div className="text-[10px] text-blue-600 dark:text-blue-400">On Leave</div>
                    </div>
                    <div className="bg-yellow-100 dark:bg-yellow-900/30 rounded-lg p-2 text-center">
                        <div className="text-sm font-bold text-yellow-600 dark:text-yellow-400">{result.halfDay}</div>
                        <div className="text-[10px] text-yellow-600 dark:text-yellow-400">Half Day</div>
                    </div>
                </div>
                {result.employeesOnLeave?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                        <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 mb-1">On Leave Today:</p>
                        {result.employeesOnLeave.slice(0, 4).map((e, i) => (
                            <div key={i} className="flex justify-between text-[10px] text-gray-600 dark:text-gray-400 py-0.5">
                                <span>{e.name}</span>
                                <span className="text-blue-500">{e.leaveType}</span>
                            </div>
                        ))}
                        {result.employeesOnLeave.length > 4 && (
                            <p className="text-[10px] text-gray-400">...and {result.employeesOnLeave.length - 4} more</p>
                        )}
                    </div>
                )}
            </div>
        );
    }

    if (name === 'get_pending_approvals') {
        const types = [
            { key: 'leave', label: 'Leave', color: 'orange', icon: '🏖️', count: result.leave?.count, requests: result.leave?.requests },
            { key: 'attendance', label: 'Attendance', color: 'blue', icon: '🕐', count: result.attendance?.count, requests: result.attendance?.requests },
            { key: 'wfh', label: 'Work From Home', color: 'purple', icon: '🏠', count: result.wfh?.count, requests: result.wfh?.requests },
            { key: 'flexi', label: 'Flexi Benefits', color: 'green', icon: '💰', count: result.flexi?.count, requests: result.flexi?.requests },
        ].filter(t => t.count > 0);

        const colorClasses = {
            orange: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
            blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
            purple: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800',
            green: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
        };

        return (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-3 mt-2 border border-gray-200 dark:border-gray-600 space-y-2">
                <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                        <span className="text-xs">📋</span>
                    </div>
                    <div>
                        <p className="text-xs font-bold text-gray-800 dark:text-gray-200">
                            {result.totalPending} Pending Approval{result.totalPending !== 1 ? 's' : ''}
                        </p>
                        <p className="text-[10px] text-gray-500 dark:text-gray-400">
                            {types.map(t => `${t.count} ${t.label}`).join(' · ')}
                        </p>
                    </div>
                </div>

                {types.map(t => (
                    <div key={t.key}>
                        <p className="text-[10px] font-semibold mb-1" style={{ color: t.color === 'orange' ? '#f97316' : t.color === 'blue' ? '#3b82f6' : t.color === 'purple' ? '#a855f7' : '#22c55e' }}>
                            {t.icon} {t.label} ({t.count})
                        </p>
                        <div className="space-y-0.5">
                            {t.requests.slice(0, 5).map((r, i) => (
                                <div key={i} className={`flex items-center justify-between text-[10px] px-2 py-1 rounded ${colorClasses[t.color]} border`}>
                                    <div className="flex-1 min-w-0">
                                        <span className="font-medium text-gray-700 dark:text-gray-300">{r.employee}</span>
                                        {r.department && <span className="text-gray-400 dark:text-gray-500 ml-1">· {r.department}</span>}
                                    </div>
                                    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 shrink-0">
                                        {t.key === 'leave' && <span>{r.leaveType} · {r.days}d</span>}
                                        {t.key === 'attendance' && <span>{r.type}</span>}
                                        {t.key === 'wfh' && <span>{r.fromDate === r.toDate ? r.fromDate : `${r.fromDate} → ${r.toDate}`}</span>}
                                        {t.key === 'flexi' && <span>₹{r.amount?.toLocaleString()}</span>}
                                    </div>
                                </div>
                            ))}
                            {t.count > 5 && (
                                <p className="text-[10px] text-gray-400 dark:text-gray-500 pl-2">+{t.count - 5} more</p>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (name === 'get_upcoming_holidays') {
        return (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 mt-2">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Upcoming Holidays ({result.count})
                </p>
                {result.holidays?.map((h, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-gray-100 dark:border-gray-600 last:border-b-0">
                        <span className="text-gray-600 dark:text-gray-400">{h.name}</span>
                        <span className="text-gray-500 dark:text-gray-400 text-[10px]">{h.date}</span>
                    </div>
                ))}
            </div>
        );
    }

    if (name === 'get_department_analytics') {
        return (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 mt-2">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Department Analytics — Total: {result.totalHeadcount}
                </p>
                <div className="space-y-1">
                    {result.departments?.map((d, i) => (
                        <div key={i} className="flex items-center justify-between text-[10px] py-0.5">
                            <span className="text-gray-600 dark:text-gray-400">{d.name}</span>
                            <div className="flex gap-2 text-gray-500 dark:text-gray-400">
                                <span className="font-medium text-gray-700 dark:text-gray-300">{d.headcount}</span>
                                {d.onboarding > 0 && <span className="text-blue-500">+{d.onboarding}</span>}
                                {d.exits30d > 0 && <span className="text-red-500">-{d.exits30d}</span>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (name === 'get_onboarding_status') {
        return (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 mt-2">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Onboarding — {result.count} employees
                </p>
                {result.employees?.map((e, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-gray-100 dark:border-gray-600 last:border-b-0">
                        <div>
                            <span className="text-gray-700 dark:text-gray-300 font-medium">{e.name}</span>
                            {e.department && <span className="text-gray-400 text-[10px] ml-1">· {e.department}</span>}
                        </div>
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">{e.joiningDate}</span>
                    </div>
                ))}
            </div>
        );
    }

    if (name === 'get_employee_list' || name === 'get_employee_info') {
        const list = result.employees || [];
        const total = result.total || list.length;
        return (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 mt-2">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Employees {result.showing ? `(${result.showing} of ${total})` : `(${total})`}
                </p>
                <div className="space-y-1">
                    {list.slice(0, 5).map((e, i) => (
                        <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-gray-100 dark:border-gray-600 last:border-b-0">
                            <div>
                                <span className="text-gray-700 dark:text-gray-300 font-medium">{e.name}</span>
                                {e.department && <span className="text-gray-400 text-[10px] ml-1">· {e.department}</span>}
                            </div>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${e.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-400'}`}>
                                {e.status}
                            </span>
                        </div>
                    ))}
                    {list.length > 5 && (
                        <p className="text-[10px] text-gray-400 mt-1">...and {list.length - 5} more</p>
                    )}
                </div>
            </div>
        );
    }

    if (name === 'get_payroll_summary') {
        if (result.found === false) {
            return (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 mt-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{result.message}</p>
                </div>
            );
        }
        return (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 mt-2 space-y-1">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Payroll — {result.periodName || `${result.month}/${result.year}`}
                </p>
                <div className={`text-xs px-2 py-1 rounded inline-block ${result.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>
                    {result.status?.charAt(0).toUpperCase() + result.status?.slice(1)}
                </div>
                <div className="grid grid-cols-3 gap-2 mt-1">
                    <div className="text-center">
                        <div className="text-xs font-bold text-gray-700 dark:text-gray-300">{result.totalEmployees}</div>
                        <div className="text-[10px] text-gray-500">Employees</div>
                    </div>
                    <div className="text-center">
                        <div className="text-xs font-bold text-green-600 dark:text-green-400">₹{(result.totalGross || 0).toLocaleString()}</div>
                        <div className="text-[10px] text-gray-500">Gross</div>
                    </div>
                    <div className="text-center">
                        <div className="text-xs font-bold text-blue-600 dark:text-blue-400">₹{(result.totalNet || 0).toLocaleString()}</div>
                        <div className="text-[10px] text-gray-500">Net</div>
                    </div>
                </div>
            </div>
        );
    }

    if (name === 'allocate_bonus') {
        const isSuccess = result.success === true;
        return (
            <div className={`${isSuccess ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'} rounded-xl p-3 mt-2`}>
                <p className={`text-xs font-semibold ${isSuccess ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'} mb-1`}>
                    {isSuccess ? '✓' : '✗'} {result.message}
                </p>
                {isSuccess && result.bonus && (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                        <div className="text-center bg-white dark:bg-gray-800 rounded-lg p-2">
                            <div className="text-xs font-bold text-gray-700 dark:text-gray-300">₹{result.bonus.amount?.toLocaleString('en-IN')}</div>
                            <div className="text-[10px] text-gray-500">{result.bonus.bonusType}</div>
                        </div>
                        <div className="text-center bg-white dark:bg-gray-800 rounded-lg p-2">
                            <div className="text-xs font-bold text-gray-700 dark:text-gray-300">{result.bonus.month}/{result.bonus.year}</div>
                            <div className="text-[10px] text-gray-500">Period</div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Salary hold — list view
    if (name === 'list_salary_holds') {
        const isSuccess = result.success === true;
        const holds = result?.data?.holds || [];
        if (!isSuccess) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3 mt-2">
                    <p className="text-xs font-medium text-red-700 dark:text-red-400">✗ {result.message}</p>
                </div>
            );
        }
        if (holds.length === 0) {
            return (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 mt-2">
                    <p className="text-xs text-gray-700 dark:text-gray-300">{result.message}</p>
                </div>
            );
        }
        return (
            <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3 mt-2">
                <p className="text-xs font-semibold text-red-700 dark:text-red-400 mb-2">
                    🚫 {holds.length} active salary hold{holds.length > 1 ? 's' : ''}
                </p>
                <div className="space-y-1.5">
                    {holds.slice(0, 8).map((h, i) => (
                        <div key={i} className="bg-white dark:bg-gray-800 rounded-lg p-2">
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                                    {h.employee}
                                </span>
                                <span className="text-[10px] text-gray-500 dark:text-gray-400 font-mono whitespace-nowrap">
                                    {h.period}
                                </span>
                            </div>
                            {h.reason && (
                                <div className="text-[10px] text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">
                                    {h.reason}
                                </div>
                            )}
                        </div>
                    ))}
                    {holds.length > 8 && (
                        <div className="text-[10px] text-gray-500 dark:text-gray-400 text-center pt-1">
                            +{holds.length - 8} more
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Write action results
    if (name === 'approve_request' || name === 'reject_request' || name === 'apply_leave_for_employee' ||
        name === 'add_holiday' || name === 'send_announcement' || name === 'mark_attendance' || name === 'create_employee' ||
        name === 'withhold_salary' || name === 'release_salary_hold') {
        const isSuccess = result.success === true;
        return (
            <div className={`${isSuccess ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'} rounded-xl p-3 mt-2`}>
                <p className={`text-xs font-medium ${isSuccess ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                    {isSuccess ? '✓' : '✗'} {result.message}
                </p>
            </div>
        );
    }

    // ─── Employee Report Card ──────────────────────────────────────────
    if (name === 'get_employee_report') {
        if (!result.found) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3 mt-2">
                    <p className="text-xs text-red-600 dark:text-red-400">{result.message}</p>
                </div>
            );
        }
        const { employee: emp, attendance: att, period } = result;
        return (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 mt-2 space-y-2.5">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">{emp.name}</p>
                        <p className="text-[10px] text-gray-500">{emp.department} · {emp.designation}</p>
                    </div>
                    <span className="text-[10px] bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded-full">{period}</span>
                </div>

                {/* Attendance Grid */}
                <div className="grid grid-cols-3 gap-1.5">
                    <div className="bg-green-100 dark:bg-green-900/30 rounded-lg p-1.5 text-center">
                        <div className="text-xs font-bold text-green-600 dark:text-green-400">{att.present}</div>
                        <div className="text-[9px] text-green-600 dark:text-green-400">Present</div>
                    </div>
                    <div className="bg-red-100 dark:bg-red-900/30 rounded-lg p-1.5 text-center">
                        <div className="text-xs font-bold text-red-600 dark:text-red-400">{att.absent}</div>
                        <div className="text-[9px] text-red-600 dark:text-red-400">Absent</div>
                    </div>
                    <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg p-1.5 text-center">
                        <div className="text-xs font-bold text-blue-600 dark:text-blue-400">{att.onLeave}</div>
                        <div className="text-[9px] text-blue-600 dark:text-blue-400">On Leave</div>
                    </div>
                    <div className="bg-yellow-100 dark:bg-yellow-900/30 rounded-lg p-1.5 text-center">
                        <div className="text-xs font-bold text-yellow-600 dark:text-yellow-400">{att.lateDays}</div>
                        <div className="text-[9px] text-yellow-600 dark:text-yellow-400">Late</div>
                    </div>
                    <div className="bg-indigo-100 dark:bg-indigo-900/30 rounded-lg p-1.5 text-center">
                        <div className="text-xs font-bold text-indigo-600 dark:text-indigo-400">{att.wfhDays}</div>
                        <div className="text-[9px] text-indigo-600 dark:text-indigo-400">WFH</div>
                    </div>
                    <div className="bg-orange-100 dark:bg-orange-900/30 rounded-lg p-1.5 text-center">
                        <div className="text-xs font-bold text-orange-600 dark:text-orange-400">{att.overtimeDays}</div>
                        <div className="text-[9px] text-orange-600 dark:text-orange-400">OT Days</div>
                    </div>
                </div>

                {/* Work Hours */}
                <div className="flex flex-wrap gap-x-3 text-[10px] text-gray-600 dark:text-gray-400">
                    <span>Total: <b className="text-gray-700 dark:text-gray-300">{att.totalWorkHours}h</b></span>
                    {att.overtimeHours > 0 && <span>OT: <b className="text-orange-600">{att.overtimeHours}h</b></span>}
                    {att.halfDay > 0 && <span>Half Days: <b>{att.halfDay}</b></span>}
                    {att.lop > 0 && <span>LOP: <b className="text-red-500">{att.lop}</b></span>}
                    {att.holiday > 0 && <span>Holidays: <b>{att.holiday}</b></span>}
                </div>

                {/* Late Arrival Details */}
                {result.lateArrivalDates?.length > 0 && (
                    <div>
                        <p className="text-[10px] font-semibold text-yellow-600 dark:text-yellow-400 mb-1">Late Arrivals:</p>
                        <div className="space-y-0.5">
                            {result.lateArrivalDates.slice(0, 5).map((l, i) => (
                                <div key={i} className="flex justify-between text-[10px] text-gray-500">
                                    <span>{l.date}</span>
                                    <span className="text-yellow-600">In: {l.checkIn}</span>
                                </div>
                            ))}
                            {result.lateArrivalDates.length > 5 && (
                                <p className="text-[9px] text-gray-400">+{result.lateArrivalDates.length - 5} more</p>
                            )}
                        </div>
                    </div>
                )}

                {/* Leave Balance */}
                {result.leaveBalance?.length > 0 && (
                    <div>
                        <p className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 mb-1">Leave Balance:</p>
                        <div className="space-y-0.5">
                            {result.leaveBalance.map((lb, i) => (
                                <div key={i} className="flex justify-between text-[10px]">
                                    <span className="text-gray-600 dark:text-gray-400">{lb.type}</span>
                                    <span className="text-gray-700 dark:text-gray-300">{lb.remaining}/{lb.entitled} <span className="text-green-500">used:{lb.used}</span></span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Leaves This Month */}
                {result.leavesThisMonth?.length > 0 && (
                    <div>
                        <p className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 mb-1">Leaves:</p>
                        {result.leavesThisMonth.map((l, i) => (
                            <div key={i} className="flex justify-between text-[10px] text-gray-600 dark:text-gray-400">
                                <span>{l.type}: {l.from} - {l.to} ({l.days}d)</span>
                                <span className={`${l.status === 'approved' ? 'text-green-500' : l.status === 'pending' ? 'text-yellow-500' : 'text-red-500'}`}>{l.status}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* WFH This Month */}
                {result.wfhThisMonth?.length > 0 && (
                    <div>
                        <p className="text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 mb-1">WFH Days:</p>
                        {result.wfhThisMonth.map((w, i) => (
                            <div key={i} className="flex justify-between text-[10px] text-gray-600 dark:text-gray-400">
                                <span>{w.date} — {w.reason}</span>
                                <span className={`${w.status === 'approved' ? 'text-green-500' : w.status === 'pending' ? 'text-yellow-500' : 'text-red-500'}`}>{w.status}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Bonuses */}
                {result.bonuses?.length > 0 && (
                    <div>
                        <p className="text-[10px] font-semibold text-green-600 dark:text-green-400 mb-1">Bonuses:</p>
                        {result.bonuses.map((b, i) => (
                            <div key={i} className="flex justify-between text-[10px] text-gray-600 dark:text-gray-400">
                                <span>{b.type}: ₹{b.amount.toLocaleString('en-IN')}</span>
                                <span className={`${b.status === 'processed' ? 'text-green-500' : 'text-yellow-500'}`}>{b.status}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Pending Approvals */}
                {result.pendingApprovals?.length > 0 && (
                    <div>
                        <p className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 mb-1">Pending:</p>
                        {result.pendingApprovals.map((p, i) => (
                            <div key={i} className="text-[10px] text-gray-500">{p.type}: {p.date} — {p.reason}</div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    // ─── Payroll Confirmation Cards ──────────────────────────────────────────
    if (name === 'execute_payroll_run' && result.needsConfirmation) {
        return (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-3 mt-2 space-y-2">
                <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                    ⚠ Confirm: Process Payroll — {result.periodName}
                </p>
                <div className="grid grid-cols-2 gap-2">
                    <div className="text-center bg-white dark:bg-gray-800 rounded-lg p-2">
                        <div className="text-sm font-bold text-gray-700 dark:text-gray-300">{result.summary?.employees || 0}</div>
                        <div className="text-[10px] text-gray-500">Employees</div>
                    </div>
                    <div className="text-center bg-white dark:bg-gray-800 rounded-lg p-2">
                        <div className="text-sm font-bold text-amber-600">{result.summary?.warnings?.length || 0}</div>
                        <div className="text-[10px] text-gray-500">Warnings</div>
                    </div>
                </div>
                {result.summary?.warnings?.map((w, i) => (
                    <p key={i} className="text-[10px] text-amber-600 dark:text-amber-400">⚠ {w}</p>
                ))}
                <p className="text-[10px] text-amber-600 dark:text-amber-400">{result.message}</p>
                <div className="flex gap-2 mt-2">
                    <button
                        onClick={() => onConfirm && onConfirm(result.action, result.month, result.year)}
                        className="flex-1 bg-green-600 text-white text-xs font-medium py-1.5 rounded-lg hover:bg-green-700 transition-colors"
                    >
                        ✓ Confirm & Process
                    </button>
                    <button
                        onClick={() => onCancel && onCancel(result.action)}
                        className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 text-xs font-medium py-1.5 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        );
    }

    if (name === 'lock_payroll_period' && result.needsConfirmation) {
        return (
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-xl p-3 mt-2 space-y-2">
                <p className="text-xs font-semibold text-purple-700 dark:text-purple-400">
                    🔒 Confirm: Lock Payroll — {result.periodName}
                </p>
                <div className="grid grid-cols-3 gap-2">
                    <div className="text-center bg-white dark:bg-gray-800 rounded-lg p-2">
                        <div className="text-sm font-bold text-gray-700 dark:text-gray-300">{result.summary?.employees || 0}</div>
                        <div className="text-[10px] text-gray-500">Employees</div>
                    </div>
                    <div className="text-center bg-white dark:bg-gray-800 rounded-lg p-2">
                        <div className="text-sm font-bold text-green-600">₹{(result.summary?.totalGross || 0).toLocaleString()}</div>
                        <div className="text-[10px] text-gray-500">Gross</div>
                    </div>
                    <div className="text-center bg-white dark:bg-gray-800 rounded-lg p-2">
                        <div className="text-sm font-bold text-blue-600">₹{(result.summary?.totalNet || 0).toLocaleString()}</div>
                        <div className="text-[10px] text-gray-500">Net</div>
                    </div>
                </div>
                <p className="text-[10px] text-purple-600 dark:text-purple-400">{result.message}</p>
                <div className="flex gap-2 mt-2">
                    <button
                        onClick={() => onConfirm && onConfirm(result.action, result.month, result.year)}
                        className="flex-1 bg-purple-600 text-white text-xs font-medium py-1.5 rounded-lg hover:bg-purple-700 transition-colors"
                    >
                        🔒 Confirm & Lock
                    </button>
                    <button
                        onClick={() => onCancel && onCancel(result.action)}
                        className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 text-xs font-medium py-1.5 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        );
    }

    // ─── Payroll Tool Result Cards ──────────────────────────────────────────
    if (name === 'initiate_payroll_run') {
        if (result.error || result.blocked) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3 mt-2">
                    <p className="text-xs font-medium text-red-600 dark:text-red-400">{result.message}</p>
                </div>
            );
        }
        if (result.alreadyProcessed) {
            return (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 mt-2">
                    <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-1">{result.periodName}</p>
                    <p className="text-xs text-blue-600 dark:text-blue-400">{result.message}</p>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                        <div className="text-center"><div className="text-xs font-bold text-blue-700 dark:text-blue-400">{result.totalEmployees}</div><div className="text-[10px] text-blue-500">Employees</div></div>
                        <div className="text-center"><div className="text-xs font-bold text-green-600">₹{(result.totalGross || 0).toLocaleString()}</div><div className="text-[10px] text-blue-500">Gross</div></div>
                        <div className="text-center"><div className="text-xs font-bold text-blue-600">₹{(result.totalNet || 0).toLocaleString()}</div><div className="text-[10px] text-blue-500">Net</div></div>
                    </div>
                </div>
            );
        }
        return (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 mt-2 space-y-2">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Payroll Pre-flight Check — {result.periodName}
                </p>
                <div className="grid grid-cols-2 gap-2">
                    <div className="bg-green-100 dark:bg-green-900/30 rounded-lg p-2 text-center">
                        <div className="text-sm font-bold text-green-600 dark:text-green-400">{result.employeesWithSalary}</div>
                        <div className="text-[10px] text-green-600 dark:text-green-400">Ready</div>
                    </div>
                    <div className="bg-yellow-100 dark:bg-yellow-900/30 rounded-lg p-2 text-center">
                        <div className="text-sm font-bold text-yellow-600 dark:text-yellow-400">{result.employeesWithoutSalary}</div>
                        <div className="text-[10px] text-yellow-600 dark:text-yellow-400">No Salary Structure</div>
                    </div>
                </div>
                {result.warnings?.length > 0 && (
                    <div className="space-y-1">
                        {result.warnings.map((w, i) => (
                            <p key={i} className="text-[10px] text-yellow-600 dark:text-yellow-400">⚠ {w}</p>
                        ))}
                    </div>
                )}
                <div className={`text-[10px] px-2 py-1 rounded inline-block ${result.canProceed ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                    {result.canProceed ? 'Ready to process' : 'Cannot proceed — fix warnings first'}
                </div>
            </div>
        );
    }

    if (name === 'execute_payroll_run') {
        if (!result.success) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3 mt-2">
                    <p className="text-xs font-medium text-red-600 dark:text-red-400">✗ {result.message}</p>
                </div>
            );
        }
        return (
            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-3 mt-2 space-y-2">
                <p className="text-xs font-semibold text-green-700 dark:text-green-400">
                    ✓ Payroll Processed — {result.periodName}
                </p>
                <div className="grid grid-cols-2 gap-2">
                    <div className="text-center bg-white dark:bg-gray-800 rounded-lg p-2">
                        <div className="text-sm font-bold text-gray-700 dark:text-gray-300">{result.totalEmployees}</div>
                        <div className="text-[10px] text-gray-500">Employees</div>
                    </div>
                    <div className="text-center bg-white dark:bg-gray-800 rounded-lg p-2">
                        <div className="text-sm font-bold text-green-600">₹{(result.totalGross || 0).toLocaleString()}</div>
                        <div className="text-[10px] text-gray-500">Gross</div>
                    </div>
                    <div className="text-center bg-white dark:bg-gray-800 rounded-lg p-2">
                        <div className="text-sm font-bold text-red-500">₹{(result.totalDeductions || 0).toLocaleString()}</div>
                        <div className="text-[10px] text-gray-500">Deductions</div>
                    </div>
                    <div className="text-center bg-white dark:bg-gray-800 rounded-lg p-2">
                        <div className="text-sm font-bold text-blue-600">₹{(result.totalNet || 0).toLocaleString()}</div>
                        <div className="text-[10px] text-gray-500">Net Pay</div>
                    </div>
                </div>
            </div>
        );
    }

    if (name === 'lock_payroll_period') {
        if (!result.success) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3 mt-2">
                    <p className="text-xs font-medium text-red-600 dark:text-red-400">✗ {result.message}</p>
                </div>
            );
        }
        return (
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3 mt-2">
                <p className="text-xs font-semibold text-purple-700 dark:text-purple-400">🔒 {result.message}</p>
                <p className="text-[10px] text-purple-500 mt-1">Period locked at {new Date(result.lockedAt).toLocaleString()}</p>
            </div>
        );
    }

    // Generic fallback — never dump raw JSON to the user. If the tool returned
    // a `message`, surface that as a clean pill; otherwise show a quiet
    // "action complete" badge. The model's natural-language reply still carries
    // the substance.
    const fallbackSuccess = result?.success !== false;
    const fallbackMessage = result?.message
        || (result?.error ? String(result.error) : (fallbackSuccess ? 'Action completed.' : 'Action failed.'));
    return (
        <div className={`${fallbackSuccess ? 'bg-gray-50 dark:bg-gray-700/50' : 'bg-red-50 dark:bg-red-900/20'} rounded-xl p-3 mt-2`}>
            <p className={`text-xs ${fallbackSuccess ? 'text-gray-700 dark:text-gray-300' : 'text-red-700 dark:text-red-400'}`}>
                {fallbackSuccess ? '✓' : '✗'} {fallbackMessage}
            </p>
        </div>
    );
};

const AIAvatar = () => {
    const AVATAR_SIZE = 64;
    const EDGE_GAP = 24;
    const POPUP_GAP = 8;
    const POSITION_STORAGE_KEY = 'oakhr_ai_avatar_position';

    const {
        isOpen,
        closeAvatar,
        hasPendingFTUE,
        currentTour,
        startTour,
        userRole,
        getAvailableTours,
        resetTour,
        getResumeData,
        clearResumeData
    } = useAIAvatar();
    const [isHovered, setIsHovered] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const [toursExpanded, setToursExpanded] = useState(false);

    // Chat state - use ChatContext
    const [showChatWindow, setShowChatWindow] = useState(false);
    const [message, setMessage] = useState('');
    const [chatEnabled] = useState(process.env.REACT_APP_AI_CHAT_ENABLED === 'true');
    const messagesEndRef = useRef(null);
    const isInitialScroll = useRef(true);

    // Chat context
    const {
        messages,
        isTyping,
        typingStatus,
        sendMessage,
        clearChat,
        aiCreditsTotal,
        aiCreditsUsed,
        aiCreditsRemaining,
        refreshCredits
    } = useChat();

    // Confirmation handlers for risky payroll actions
    const handleConfirmAction = useCallback((action, month, year) => {
        sendMessage(`[CONFIRM:${action}|month=${month}&year=${year}]`);
    }, [sendMessage]);

    const handleCancelAction = useCallback((action) => {
        sendMessage(`[CANCEL:${action}]`);
    }, [sendMessage]);

    // Auto-hide "Need help?" tooltip after MAX_TOOLTIP_VIEWS visits
    const [showHelpTooltip, setShowHelpTooltip] = useState(() => {
        try {
            const views = parseInt(localStorage.getItem(HELP_TOOLTIP_VIEWS_KEY) || '0', 10);
            return views < MAX_TOOLTIP_VIEWS;
        } catch { return true; }
    });

    useEffect(() => {
        // Increment view count on mount
        try {
            const views = parseInt(localStorage.getItem(HELP_TOOLTIP_VIEWS_KEY) || '0', 10);
            const newViews = views + 1;
            localStorage.setItem(HELP_TOOLTIP_VIEWS_KEY, String(newViews));
            if (newViews >= MAX_TOOLTIP_VIEWS) {
                setShowHelpTooltip(false);
            }
        } catch { /* ignore */ }
    }, []);

    const [isDragging, setIsDragging] = useState(false);
    const [menuPosition, setMenuPosition] = useState(null);
    const [viewportSize, setViewportSize] = useState(() => ({
        width: typeof window === 'undefined' ? 1440 : window.innerWidth,
        height: typeof window === 'undefined' ? 900 : window.innerHeight
    }));
    const [avatarPosition, setAvatarPosition] = useState(() => {
        if (typeof window === 'undefined') {
            return { x: EDGE_GAP, y: EDGE_GAP };
        }

        try {
            const savedPosition = localStorage.getItem(POSITION_STORAGE_KEY);
            if (savedPosition) {
                const parsed = JSON.parse(savedPosition);
                if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
                    return parsed;
                }
            }
        } catch (error) {
            console.error('Failed to read avatar position from storage:', error);
        }

        return {
            x: Math.max(EDGE_GAP, window.innerWidth - AVATAR_SIZE - EDGE_GAP),
            y: Math.max(EDGE_GAP, window.innerHeight - AVATAR_SIZE - EDGE_GAP)
        };
    });
    const menuRef = useRef(null);
    const avatarButtonRef = useRef(null);
    const menuPopupRef = useRef(null);
    const dragStateRef = useRef({
        isDragging: false,
        moved: false,
        pointerId: null,
        startX: 0,
        startY: 0,
        originX: 0,
        originY: 0
    });

    const clampPosition = (x, y) => {
        const maxX = Math.max(EDGE_GAP, window.innerWidth - AVATAR_SIZE - EDGE_GAP);
        const maxY = Math.max(EDGE_GAP, window.innerHeight - AVATAR_SIZE - EDGE_GAP);
        return {
            x: Math.min(Math.max(x, EDGE_GAP), maxX),
            y: Math.min(Math.max(y, EDGE_GAP), maxY)
        };
    };

    const getPopupPosition = useCallback((popupWidth, popupHeight, anchorRect) => {
        // Default: align popup right edge to avatar right edge and place above avatar
        let left = anchorRect.right - popupWidth;
        let top = anchorRect.top - popupHeight - POPUP_GAP;

        const minLeft = EDGE_GAP;
        const maxLeft = Math.max(EDGE_GAP, viewportSize.width - popupWidth - EDGE_GAP);
        left = Math.min(Math.max(left, minLeft), maxLeft);

        // If not enough room above, place below avatar
        if (top < EDGE_GAP) {
            top = anchorRect.bottom + POPUP_GAP;
        }

        // If still overflowing bottom, force to max possible visible top
        const maxTop = Math.max(EDGE_GAP, viewportSize.height - popupHeight - EDGE_GAP);
        top = Math.min(Math.max(top, EDGE_GAP), maxTop);

        return { left, top };
    }, [viewportSize]);

    // Close menu/chat when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowMenu(false);
                setToursExpanded(false);
                setShowChatWindow(false);
            }
        };

        if (showMenu || showChatWindow) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showMenu, showChatWindow]);

    // Mouse follow effect for 3D feel
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);

    const mouseXSpring = useSpring(mouseX, { stiffness: 300, damping: 20 });
    const mouseYSpring = useSpring(mouseY, { stiffness: 300, damping: 20 });

    const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], [10, -10]);
    const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], [-10, 10]);

    const handleMouseMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        mouseX.set((e.clientX - centerX) / window.innerWidth);
        mouseY.set((e.clientY - centerY) / window.innerHeight);
    };

    const handleMouseLeave = () => {
        mouseX.set(0);
        mouseY.set(0);
        setIsHovered(false);
    };

    const pendingBadge = hasPendingFTUE() || currentTour;
    const availableTours = getAvailableTours();

    const getStepIndexByTarget = (tourId, target) => {
        const tour = getTourById(tourId, userRole);
        if (!tour?.steps?.length) return 0;
        const index = tour.steps.findIndex((step) => step.target === target);
        return index >= 0 ? index : 0;
    };

    const fullTourIds = ['admin-welcome-tour', 'admin-onboarding-tour', 'admin-advanced-tour'];
    const fullTours = availableTours.filter((tour) => fullTourIds.includes(tour.id));

    const baseSectionTours = availableTours.filter((tour) => !fullTourIds.includes(tour.id));
    const dashboardSection = {
        id: 'admin-welcome-tour__section_dashboard',
        title: 'Dashboard',
        completed: false,
        autoStart: false,
        baseTourId: 'admin-welcome-tour',
        startStep: getStepIndexByTarget('admin-welcome-tour', '.tour-admin-dashboard-header')
    };
    const vibeSection = {
        id: 'admin-welcome-tour__section_vibe',
        title: 'Vibe',
        completed: false,
        autoStart: false,
        baseTourId: 'admin-welcome-tour',
        startStep: getStepIndexByTarget('admin-welcome-tour', '.tour-admin-dashboard-vibe-section')
    };
    const orgViewSection = {
        id: 'admin-welcome-tour__section_org_view',
        title: 'Org View',
        completed: false,
        autoStart: false,
        baseTourId: 'admin-welcome-tour',
        startStep: getStepIndexByTarget('admin-welcome-tour', '.tour-org-view')
    };
    const sectionTours = [dashboardSection, vibeSection, orgViewSection, ...baseSectionTours];

    const handleAvatarClick = (e) => {
        e.stopPropagation();
        if (dragStateRef.current.moved) {
            dragStateRef.current.moved = false;
            return;
        }
        if (currentTour) return; // Don't open menu during active tour
        // Close chat window if open, otherwise toggle menu
        if (showChatWindow) {
            setShowChatWindow(false);
        } else {
            setShowMenu(prev => !prev);
            setToursExpanded(false);
        }
    };

    const handleDragStart = (e) => {
        if (e.button !== undefined && e.button !== 0) return;

        dragStateRef.current = {
            isDragging: true,
            moved: false,
            pointerId: e.pointerId ?? null,
            startX: e.clientX,
            startY: e.clientY,
            originX: avatarPosition.x,
            originY: avatarPosition.y
        };
        setIsDragging(true);
    };

    useEffect(() => {
        const handlePointerMove = (e) => {
            if (!dragStateRef.current.isDragging) return;

            const deltaX = e.clientX - dragStateRef.current.startX;
            const deltaY = e.clientY - dragStateRef.current.startY;
            if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
                dragStateRef.current.moved = true;
            }

            const nextX = dragStateRef.current.originX + deltaX;
            const nextY = dragStateRef.current.originY + deltaY;
            setAvatarPosition(clampPosition(nextX, nextY));
        };

        const handlePointerUp = () => {
            if (!dragStateRef.current.isDragging) return;
            dragStateRef.current.isDragging = false;
            setIsDragging(false);
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);

        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(avatarPosition));
        } catch (error) {
            console.error('Failed to persist avatar position:', error);
        }
    }, [avatarPosition]);

    useEffect(() => {
        const handleResize = () => {
            setViewportSize({ width: window.innerWidth, height: window.innerHeight });
            setAvatarPosition((current) => clampPosition(current.x, current.y));
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useLayoutEffect(() => {
        if (!showMenu || !avatarButtonRef.current || !menuPopupRef.current) return;
        const anchorRect = avatarButtonRef.current.getBoundingClientRect();
        const popupRect = menuPopupRef.current.getBoundingClientRect();
        setMenuPosition(getPopupPosition(popupRect.width, popupRect.height, anchorRect));
    }, [showMenu, toursExpanded, avatarPosition, viewportSize, getPopupPosition]);

    const resumeData = getResumeData();

    const handleStartTour = (tourOption) => {
        setShowMenu(false);
        const baseTourId = tourOption.baseTourId || tourOption.id;
        const startStep = Number.isInteger(tourOption.startStep) ? tourOption.startStep : 0;

        // Check if there's saved resume data for this tour
        if (resumeData && resumeData.tourId === baseTourId && startStep === 0) {
            startTour(baseTourId, resumeData.lastStep);
            clearResumeData();
            return;
        }

        // If full/base tour was completed and user starts it from beginning, reset first.
        if (startStep === 0 && availableTours.find((t) => t.id === baseTourId)?.completed) {
            resetTour(baseTourId);
        }
        startTour(baseTourId, startStep);
    };

    // Chat functions
    const navigate = useNavigate();

    const scrollToBottom = (instant = false) => {
        messagesEndRef.current?.scrollIntoView({ behavior: instant ? 'auto' : 'smooth' });
    };

    useEffect(() => {
        if (isInitialScroll.current && messages.length > 0) {
            // On first load, jump to bottom instantly
            scrollToBottom(true);
            isInitialScroll.current = false;
        } else if (!isInitialScroll.current) {
            // For new messages, smooth scroll
            scrollToBottom(false);
        }
    }, [messages]);

    // Scroll to bottom when chat window opens
    useEffect(() => {
        if (showChatWindow && messages.length > 0) {
            requestAnimationFrame(() => {
                scrollToBottom(true);
            });
        }
    }, [showChatWindow]);

    const handleAction = (action) => {
        if (action.type === 'navigate') {
            navigate(action.path);
            setShowChatWindow(false);
        }
    };

    const handleOpenChat = () => {
        setShowMenu(false);
        setShowChatWindow(true);
        // Reset so next scroll jumps to bottom instantly
        isInitialScroll.current = true;
    };

    const handleCloseChat = () => {
        setShowChatWindow(false);
    };

    const handleSend = async () => {
        if (!message.trim()) return;
        const userMessageText = message;
        setMessage('');
        await sendMessage(userMessageText);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <div
            ref={menuRef}
            className="fixed z-[9999] flex flex-col items-end gap-2"
            style={{ left: `${avatarPosition.x}px`, top: `${avatarPosition.y}px` }}
        >
            {/* Grouped Menu Dropdown */}
            <AnimatePresence>
                {showMenu && !currentTour && (
                    <motion.div
                        ref={menuPopupRef}
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="fixed w-72 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                        style={{
                            left: `${(menuPosition?.left ?? -9999)}px`,
                            top: `${(menuPosition?.top ?? -9999)}px`,
                            visibility: menuPosition ? 'visible' : 'hidden'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                                    <span className="text-xl">🤖</span>
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-white font-semibold">Hi! I'm here to help</h3>
                                    <p className="text-white/80 text-sm">How can I assist you today?</p>
                                </div>
                            </div>
                        </div>

                        {/* Options */}
                        <div className="p-2">
                            {/* Chat with AI - Opens separate window */}
                            <button
                                onClick={handleOpenChat}
                                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors text-left group"
                            >
                                <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                                    <MessageSquare className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                </div>
                                <span className="font-medium text-gray-900 dark:text-white">Chat with AI</span>
                                <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
                            </button>

                            {/* Tours Section */}
                            <div>
                                <button
                                    onClick={() => setToursExpanded(!toursExpanded)}
                                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors text-left group"
                                >
                                    <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                                        <Route className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                                    </div>
                                    <span className="font-medium text-gray-900 dark:text-white">Guided Tours</span>
                                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${toursExpanded ? 'rotate-180' : ''} ml-auto`} />
                                </button>

                                {/* Expanded Tour List */}
                                {toursExpanded && (
                                    <div className="ml-4 pl-4 border-l-2 border-indigo-200 dark:border-indigo-700 py-1 max-h-64 overflow-y-auto pr-1">
                                        {availableTours.length === 0 ? (
                                            <p className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">No tours available</p>
                                        ) : (
                                            <>
                                                {fullTours.length > 0 && (
                                                    <div className="mb-2">
                                                        <div className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                            Full Tour
                                                        </div>
                                                        {fullTours.map((tour) => {
                                                            const hasResume = resumeData && resumeData.tourId === tour.id;
                                                            return (
                                                                <button
                                                                    key={tour.id}
                                                                    onClick={() => handleStartTour(tour)}
                                                                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors text-left text-sm"
                                                                >
                                                                    {tour.completed ? (
                                                                        <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                                                                    ) : (
                                                                        <Circle className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                                                                    )}
                                                                    <span className="flex-1 text-left text-gray-900 dark:text-white">{tour.title}</span>
                                                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                        {hasResume ? 'Resume' : tour.completed ? 'Done' : tour.autoStart ? 'New' : ''}
                                                                    </span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                )}

                                                {sectionTours.length > 0 && (
                                                    <div className="mb-2">
                                                        <div className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                            Section Tours
                                                        </div>
                                                        {sectionTours.map((tour) => {
                                                            const baseTourId = tour.baseTourId || tour.id;
                                                            const hasResume = resumeData && resumeData.tourId === baseTourId;
                                                            return (
                                                                <button
                                                                    key={tour.id}
                                                                    onClick={() => handleStartTour(tour)}
                                                                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors text-left text-sm"
                                                                >
                                                                    {tour.completed ? (
                                                                        <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                                                                    ) : (
                                                                        <Circle className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                                                                    )}
                                                                    <span className="flex-1 text-left text-gray-900 dark:text-white">{tour.title}</span>
                                                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                        {hasResume ? 'Resume' : tour.completed ? 'Done' : tour.autoStart ? 'New' : ''}
                                                                    </span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Chat Sidebar */}
            <AnimatePresence>
                {showChatWindow && !currentTour && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="fixed inset-0 bg-black/30 z-[10000]"
                            onClick={handleCloseChat}
                        />
                        {/* Sidebar */}
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                            className="fixed right-0 top-0 h-full w-[min(380px,100vw)] bg-white dark:bg-gray-800 shadow-2xl border-l border-gray-200 dark:border-gray-700 flex flex-col z-[10001]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="bg-gradient-to-r from-purple-500 via-purple-600 to-indigo-600 p-4 flex-shrink-0">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                                        <span className="text-xl">🤖</span>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-white font-semibold">Chat with AI</h3>
                                        <p className="text-white/80 text-sm">{chatEnabled ? 'Online' : 'Demo Mode'}</p>
                                    </div>
                                    <button
                                        onClick={() => { clearChat(); }}
                                        className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm hover:bg-white/30 transition-colors"
                                        title="New Chat"
                                    >
                                        <Plus className="w-4 h-4 text-white" />
                                    </button>
                                    <button
                                        onClick={handleCloseChat}
                                        className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm hover:bg-white/30 transition-colors"
                                        title="Close"
                                    >
                                        <X className="w-4 h-4 text-white" />
                                    </button>
                                </div>
                                {/* AI Credits Bar */}
                                {aiCreditsTotal !== null && aiCreditsTotal !== undefined && (
                                    <div className="mt-3 px-1">
                                        <div className="flex items-center justify-between text-[10px] text-white/70 mb-1">
                                            <span>AI Tool Credits</span>
                                            <span>
                                                {aiCreditsTotal === null ? 'Unlimited' : `${aiCreditsRemaining} / ${aiCreditsTotal} remaining`}
                                            </span>
                                        </div>
                                        {aiCreditsTotal !== null && (
                                            <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full rounded-full transition-all duration-500 ${
                                                        (aiCreditsUsed / aiCreditsTotal) > 0.9
                                                            ? 'bg-red-400'
                                                            : (aiCreditsUsed / aiCreditsTotal) > 0.7
                                                            ? 'bg-amber-400'
                                                            : 'bg-white/70'
                                                    }`}
                                                    style={{ width: `${Math.max(0, Math.min(100, ((aiCreditsTotal - aiCreditsUsed) / aiCreditsTotal) * 100))}%` }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                                {aiCreditsTotal === null && (
                                    <div className="mt-2 flex items-center gap-1.5 text-[10px] text-white/60">
                                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400"></span>
                                        Unlimited AI credits
                                    </div>
                                )}
                            </div>

                            {/* Messages */}
                            <div className="flex-1 overflow-y-auto p-4">
                                <div className="space-y-3">
                                    {/* Suggested prompts when no messages */}
                                    {messages.length === 0 && !isTyping && (
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-1.5 mb-3">
                                                <Sparkles className="w-3.5 h-3.5 text-purple-500" />
                                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Try asking</span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                {getSuggestedPrompts(userRole).map((sp, idx) => (
                                                    <button
                                                        key={idx}
                                                        onClick={() => { setMessage(''); sendMessage(sp.prompt); }}
                                                        className="px-3 py-2.5 text-left text-xs bg-gray-50 dark:bg-gray-700/50 hover:bg-purple-50 dark:hover:bg-purple-900/20 border border-gray-200 dark:border-gray-600 rounded-xl transition-colors text-gray-700 dark:text-gray-300 hover:text-purple-700 dark:hover:text-purple-300 hover:border-purple-200 dark:hover:border-purple-700"
                                                    >
                                                        {sp.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {messages.map((msg) => {
                                        // Hide internal confirmation/cancel messages
                                        if (msg.role === 'user' && (msg.content?.match(/^\[CONFIRM:/) || msg.content?.match(/^\[CANCEL:/))) {
                                            return null;
                                        }
                                        return (
                                        <div key={msg.id} className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                                            {msg.role === 'assistant' && (
                                                <div className="w-6 h-6 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                                    <span className="text-xs">🤖</span>
                                                </div>
                                            )}
                                            <div className={`max-w-[300px] px-3 py-2 rounded-2xl ${
                                                msg.role === 'user'
                                                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-sm'
                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                            }`}>
                                                {msg.role === 'user' ? (
                                                    <p className="whitespace-pre-wrap break-words text-sm">{msg.content}</p>
                                                ) : (
                                                    <div className="space-y-0.5 break-words">
                                                        {renderMarkdown(msg.content)}
                                                        {msg.toolCalls?.map((tc, i) => (
                                                            <ToolResultCard
                                                                key={i}
                                                                toolCall={tc}
                                                                onConfirm={handleConfirmAction}
                                                                onCancel={handleCancelAction}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            {msg.role === 'user' && (
                                                <div className="w-6 h-6 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                                    <span className="text-xs">👤</span>
                                                </div>
                                            )}
                                        </div>
                                        );
                                    })}
                                    {isTyping && (
                                        <div className="flex gap-2">
                                            <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                                                <span className="text-sm">🤖</span>
                                            </div>
                                            <div className="px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-2xl">
                                                {typingStatus ? (
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 italic">{typingStatus}</p>
                                                ) : (
                                                    <div className="flex gap-1">
                                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>
                            </div>

                            {/* Action Buttons */}
                            {messages[messages.length - 1]?.actions && messages[messages.length - 1].actions.length > 0 && (
                                <div className="px-4 pb-2 flex-shrink-0">
                                    <div className="flex flex-wrap gap-2">
                                        {messages[messages.length - 1].actions.map((action, idx) => (
                                            <button
                                                key={idx}
                                                onClick={() => handleAction(action)}
                                                className="px-3 py-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-full text-xs hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
                                            >
                                                {action.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Input */}
                            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
                                {/* Credits exhausted warning */}
                                {aiCreditsTotal !== null && aiCreditsRemaining <= 0 && (
                                    <div className="mb-3 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
                                        <p className="text-xs text-amber-700 dark:text-amber-400 font-medium mb-2">
                                            AI tool credits exhausted for this month
                                        </p>
                                        <p className="text-[10px] text-amber-600 dark:text-amber-500 mb-2">
                                            You can still chat, but data lookups and actions require credits. Upgrade for more.
                                        </p>
                                        <button
                                            onClick={() => { navigate('/admin/billing'); setShowChatWindow(false); }}
                                            className="w-full py-2 text-xs font-medium bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors"
                                        >
                                            Upgrade Plan
                                        </button>
                                    </div>
                                )}
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        placeholder="Ask about HR..."
                                        className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                                    />
                                    <button
                                        onClick={handleSend}
                                        disabled={!message.trim() || isTyping}
                                        className="w-10 h-10 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 text-white rounded-full flex items-center justify-center transition-colors"
                                        title="Send"
                                    >
                                        <Send className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* Avatar Button */}
            <motion.div
                ref={avatarButtonRef}
                style={{
                    rotateX,
                    rotateY,
                    transformStyle: 'preserve-3d'
                }}
                onPointerDown={handleDragStart}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                onMouseEnter={() => setIsHovered(true)}
                onClick={handleAvatarClick}
                className={`tour-ai-fab relative p-2 -m-2 ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            >
                {/* Glow effect */}
                <motion.div
                    className="absolute inset-0 rounded-full"
                    animate={{
                        scale: isHovered ? 1.2 : 1,
                        opacity: isHovered ? 0.6 : 0.3
                    }}
                    transition={{ duration: 0.3 }}
                    style={{
                        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.5) 0%, rgba(99, 102, 241, 0) 70%)'
                    }}
                />

                {/* Avatar container */}
                <motion.div
                    className="relative w-16 h-16 rounded-full"
                    animate={{
                        y: isHovered ? -4 : 0,
                        scale: isHovered ? 1.05 : 1
                    }}
                    transition={{
                        type: 'spring',
                        stiffness: 400,
                        damping: 17
                    }}
                >
                    {/* 3D-style robot avatar */}
                    <svg
                        viewBox="0 0 100 100"
                        className="w-full h-full drop-shadow-lg"
                        style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' }}
                    >
                        {/* Robot head base */}
                        <defs>
                            <linearGradient id="metalGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#e0e7ff" />
                                <stop offset="50%" stopColor="#c7d2fe" />
                                <stop offset="100%" stopColor="#a5b4fc" />
                            </linearGradient>
                            <linearGradient id="screenGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor="#60a5fa" />
                                <stop offset="100%" stopColor="#3b82f6" />
                            </linearGradient>
                            <filter id="glow">
                                <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                                <feMerge>
                                    <feMergeNode in="coloredBlur"/>
                                    <feMergeNode in="SourceGraphic"/>
                                </feMerge>
                            </filter>
                        </defs>

                        {/* Head outline */}
                        <ellipse cx="50" cy="50" rx="38" ry="35" fill="url(#metalGradient)" stroke="#818cf8" strokeWidth="2"/>

                        {/* Face screen */}
                        <rect x="20" y="30" width="60" height="40" rx="12" fill="url(#screenGradient)" filter="url(#glow)"/>

                        {/* Eyes - animated blink */}
                        <g className="avatar-eyes">
                            <ellipse cx="35" cy="48" rx="6" ry="8" fill="#1e3a8a" opacity="0.8"/>
                            <ellipse cx="65" cy="48" rx="6" ry="8" fill="#1e3a8a" opacity="0.8"/>
                            {/* Eye shine */}
                            <circle cx="37" cy="45" r="2" fill="#93c5fd" opacity="0.9"/>
                            <circle cx="67" cy="45" r="2" fill="#93c5fd" opacity="0.9"/>
                        </g>

                        {/* Mouth - friendly smile */}
                        <path d="M 38 58 Q 50 65 62 58" stroke="#1e3a8a" strokeWidth="2" fill="none" strokeLinecap="round"/>

                        {/* Antenna */}
                        <line x1="50" y1="15" x2="50" y2="5" stroke="#818cf8" strokeWidth="2"/>
                        <circle cx="50" cy="3" r="3" fill="#f472b6">
                            <animate
                                attributeName="opacity"
                                values="0.5;1;0.5"
                                dur="2s"
                                repeatCount="indefinite"
                            />
                        </circle>

                        {/* Ears/headphones */}
                        <rect x="8" y="40" width="8" height="20" rx="4" fill="#6366f1"/>
                        <rect x="84" y="40" width="8" height="20" rx="4" fill="#6366f1"/>
                    </svg>

                    {/* Pending badge */}
                    {pendingBadge && !isOpen && (
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center"
                        >
                            <span className="text-white text-xs font-bold">!</span>
                        </motion.div>
                    )}
                </motion.div>

                {/* Speech bubble hint when closed - auto-hides after a few visits */}
                {!isOpen && !currentTour && showHelpTooltip && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="absolute right-full mr-4 top-0 bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-lg border border-gray-200 dark:border-gray-700 whitespace-nowrap"
                    >
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            Need help?
                        </span>
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-l-8 border-l-white dark:border-l-gray-800" />
                    </motion.div>
                )}
            </motion.div>

            {/* Quick action buttons when open */}
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex gap-2"
                >
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            closeAvatar();
                        }}
                        className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                        title="Minimize"
                    >
                        <Minimize2 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            closeAvatar();
                        }}
                        className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center hover:bg-red-600 transition-colors"
                        title="Close"
                    >
                        <X className="w-5 h-5 text-white" />
                    </button>
                </motion.div>
            )}

            {/* Blink animation CSS */}
            <style>{`
                .avatar-eyes {
                    animation: blink 4s infinite;
                }
                @keyframes blink {
                    0%, 96%, 100% { transform: scaleY(1); }
                    98% { transform: scaleY(0.1); }
                }
            `}</style>
        </div>
    );
};

export default AIAvatar;
